import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {useLocation, useNavigationType} from 'react-router';
import posthog from 'posthog-js';
import {Cookies} from 'react-cookie-consent';
import {POSTHOG_KEY, POSTHOG_HOST} from './Util/shared-constants';
import {isReplacedLegacyAddress, routeTemplate} from './Util/routeTemplates';

// eslint-disable-next-line react-refresh/only-export-components
export const POSTHOG_ENABLED = !!POSTHOG_KEY && !!POSTHOG_HOST;

// eslint-disable-next-line react-refresh/only-export-components
export const COMPASS_APPLICATIONS = {
  YOUNG_PERSON: 'young-person-app',
  PARENT: 'parent-app',
  PROFESSIONAL: 'professional-app',
  LAUNCH: 'launch-app',
  REPORT: 'report-app',
};

const buildCurrentUrl = () => window.location.origin + window.location.pathname;

/**
 * The page's route, `compass_route` (COM-470): one for every address of a page, whether it
 * names its record by an old id, a slug or a ULID (`Util/routeTemplates.ts`).
 */
const currentRoute = () => routeTemplate(window.location.pathname);

/** The consent banner's answer, which is PostHog's record of consent as well. */
// eslint-disable-next-line react-refresh/only-export-components
export const hasAnalyticsConsent = () => Cookies.get('compassCookieConsent') === 'true';

/**
 * Ends the signed-in person's PostHog identity, for a logout (COM-457). The three
 * applications share one origin, and so PostHog's one distinct ID: without this, whoever
 * signs in next on the same browser, in any role, is counted as the person who left.
 *
 * `reset()` also clears PostHog's own record of consent, which `opt_out_capturing_by_default`
 * then reads as a refusal, so consent is restored from the banner's cookie, as it is on
 * every load, and without an `$opt_in` event: nobody has just given it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const resetAnalyticsIdentity = () => {
  if (!POSTHOG_ENABLED) {
    return;
  }
  posthog.reset();
  // `reset()` also forgot the page's route, which every event PostHog sends is to carry.
  posthog.register({compass_route: currentRoute()});
  if (hasAnalyticsConsent()) {
    posthog.opt_in_capturing({captureEventName: false});
  }
};

export const PostHogAnalyticsProvider = ({children}) => {
  useEffect(() => {
    if (POSTHOG_ENABLED) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: false,
        autocapture: true,
        opt_out_capturing_by_default: true,
      });
      // The page's own `usePageTracking` registered its route before this, as a child's
      // effects run before its parent's, and PostHog keeps nothing before it has started. So
      // the page PostHog starts on is registered here, or its autocaptured events would carry
      // whatever route PostHog stored on an earlier visit.
      posthog.register({compass_route: currentRoute()});

      // Restore consent for returning users
      if (hasAnalyticsConsent()) {
        posthog.opt_in_capturing();
      }
    } else {
      console.log('Note: PostHog is not configured in this environment');
    }
  }, []);

  return children;
};

/**
 * Sends a `$pageview` for each page an application shows, with the page's route as
 * `compass_route` (COM-470), and only with analytics consent. Each page also registers its
 * route with PostHog, consent or not: that sends nothing, and PostHog adds it to every event
 * it does send, the ones it autocaptures included.
 *
 * Both happen as the page is committed, in a layout effect, which runs before every
 * `useEffect` of the page: an event the page captures as it opens, such as the legacy-id event
 * of a replace in place whose record is already loaded, carries its route, and its pageview
 * comes first, with the address it opened at.
 *
 * The pageview of the address `useCanonicalRouteParams` (COM-469) replaces an old one with
 * carries `canonicalised_from_legacy_id: true` when it is the visit's second: when PostHog
 * sent the old address's own. It did not on a document opened at the old address, as PostHog
 * starts after the first render and drops what it is given before, and then the pageview
 * after the replace is the visit's one, unflagged. Only the replace itself: Back or a reload
 * to that entry keeps its state, and is a visit.
 *
 * No pageview is sent for an address that names a record by the old numeric id of a page
 * that has moved (`isReplacedLegacyAddress`, COM-474), wherever it came from: a link, a
 * replayed sign-in destination or a document load. Its route is still registered, so the
 * legacy-id event of the replace carries it, and the pageview of the address that replaces
 * it is the visit's one, unflagged. An old id the page finds no record for has none.
 *
 * `client` stands in for PostHog: the gallery, where PostHog is off, passes a recorder. Its
 * `capture` returns the event it sends, or nothing for one it drops, as PostHog's does.
 *
 * @param {string} [compassApplication] One of `COMPASS_APPLICATIONS`.
 * @param {{client?: {capture: (event: string, properties: Record<string, unknown>) => unknown, register: (properties: Record<string, unknown>) => void} | null}} [options]
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePageTracking = (compassApplication, {client = POSTHOG_ENABLED ? posthog : null} = {}) => {
  const location = useLocation();
  const canonicalised = useNavigationType() === 'REPLACE' && Boolean(location.state?.canonicalisedFrom);
  // Whether PostHog sent the last location's pageview.
  const lastPageviewSent = useRef(false);

  useLayoutEffect(() => {
    if (!client) {
      return;
    }
    const compass_route = currentRoute();
    client.register({compass_route});
    let sent = false;
    if (hasAnalyticsConsent() && !isReplacedLegacyAddress(window.location.pathname)) {
      const properties = {$current_url: buildCurrentUrl(), compass_route};
      if (compassApplication) {
        properties.compass_application = compassApplication;
      }
      if (canonicalised && lastPageviewSent.current) {
        properties.canonicalised_from_legacy_id = true;
      }
      sent = Boolean(client.capture('$pageview', properties));
    }
    lastPageviewSent.current = sent;
  }, [location.pathname, compassApplication]);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCompassEventTracking = () => {
  // Any further property is sent beside these, such as a share's `resource_slug` (COM-481).
  return useCallback(({category, action, name, value, ...properties}) => {
    if (!POSTHOG_ENABLED) {
      return;
    }
    posthog.capture(action, {
      category,
      name,
      value,
      ...properties,
    });
  }, []);
};
